import { request, type RequestOptions } from '@nextgen-web-framework/core';
import { VersionId, SynonymPK, GroupName, EntryId } from '../config/searchApiConstants';
import {
  deleteSynonymGroupReqObj,
  getIndexHistoryEntriesReqObj,
  getSearchReqObj,
  getSynonymGroupsReqObj,
  getSynonymReqObj,
  getCreateSynonymGroupReqObj,
  updateIndexReqObj,
  getUpdateSynonymGroupReqObj,
  getSynonymByGroupNameReqObj,
  getLowQualityResultsReqObj,
  lowQualityResultEntryReqObj,
  updateLowQualityResultEntryReqObj,
  getContentSearchReqObj,
} from '../config/searchApiEndPoints';
import {
  IndexOperationResponse,
  ProductSearch,
  SearchDictionaryEntry,
  SearchDictionary,
  IndexHistory,
  LowQualitySearchHistory,
  LowQualitySearchHistoryEntry,
  UpdateLowQualitySearchEntry,
  ContentSearch,
} from '../models';
import {
  AdditionalRequestOptions,
  SaveSynonymGroupPayload,
  SearchApiProp,
  SynonymGroupByNamePayload,
  SynonymGroupsPayload,
  UpdateSynonymGroupPayload,
  ContectSearchPayload,
} from './types/types';

export const searchApi = {
  getSearchResults({ payload }: SearchApiProp, options?: AdditionalRequestOptions): Promise<ProductSearch> {
    const req = { ...getSearchReqObj };
    if (!payload) {
      return Promise.reject('Please check arguments');
    }
    req.url = req.url.replace(VersionId, 'v1');
    req.params = Array.isArray(payload) ? payload : { ...payload };

    return handleRequest(req, options);
  },
  getSynonymGroups(
    { searchterm = '', amwayCountry, amwayLanguage }: SynonymGroupsPayload,
    options?: AdditionalRequestOptions,
  ): Promise<SearchDictionary> {
    const req = getSynonymGroupsReqObj(amwayCountry, amwayLanguage);
    req.url = req.url.replace(VersionId, 'v1');
    req.params = { searchterm };
    return handleRequest(req, options);
  },
  getSynonymGroupDetail(synonymPk: string, options?: AdditionalRequestOptions): Promise<SearchDictionaryEntry> {
    const req = { ...getSynonymReqObj };
    req.url = req.url.replace(VersionId, 'v1');
    req.url = req.url.replace(SynonymPK, synonymPk);
    return handleRequest(req, options);
  },
  getSynonymByGroupName(
    { groupName, amwayCountry, amwayLanguage }: SynonymGroupByNamePayload,
    options?: AdditionalRequestOptions,
  ): Promise<SearchDictionaryEntry> {
    const req = getSynonymByGroupNameReqObj(amwayCountry, amwayLanguage);
    req.url = req.url.replace(VersionId, 'v1');
    req.url = req.url.replace(GroupName, groupName);
    return handleRequest(req, options);
  },
  saveSynonymGroup(
    { payload, amwayCountry, amwayLanguage }: SaveSynonymGroupPayload,
    options?: AdditionalRequestOptions,
  ): Promise<SearchDictionaryEntry> {
    const req = getCreateSynonymGroupReqObj(amwayCountry, amwayLanguage);
    req.url = req.url.replace(VersionId, 'v1');
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
  getContentSearch({ payload }: ContectSearchPayload, options?: AdditionalRequestOptions): Promise<ContentSearch> {
    const req = { ...getContentSearchReqObj };
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
  deleteSynonymGroup(synonymPk: string, options?: AdditionalRequestOptions): Promise<void> {
    const req = { ...deleteSynonymGroupReqObj };
    req.url = req.url.replace(VersionId, 'v1');
    req.url = req.url.replace(SynonymPK, synonymPk);
    return handleRequest(req, options);
  },
  updateSynonymGroup(
    { payload, amwayCountry, amwayLanguage }: UpdateSynonymGroupPayload,
    options?: AdditionalRequestOptions,
  ): Promise<SearchDictionaryEntry> {
    const req = getUpdateSynonymGroupReqObj(amwayCountry, amwayLanguage);
    req.url = req.url.replace(VersionId, 'v1');
    req.url = req.url.replace(SynonymPK, payload.synonymPk);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
  updateIndex(options?: AdditionalRequestOptions): Promise<IndexOperationResponse> {
    const req = { ...updateIndexReqObj };
    req.url = req.url.replace(VersionId, 'v1');
    req.params = { operation: 'REINDEX' };
    req.body = {} as unknown as BodyInit;
    return handleRequest(req, options);
  },
  getIndexHistoryEntries(options?: AdditionalRequestOptions): Promise<IndexHistory> {
    const req = { ...getIndexHistoryEntriesReqObj };
    req.url = req.url.replace(VersionId, 'v1');
    req.params = { operation: 'REINDEX' };
    return handleRequest(req, options);
  },
  getLowQualityResults(language?: string, options?: AdditionalRequestOptions): Promise<LowQualitySearchHistory> {
    const req = getLowQualityResultsReqObj(language);
    req.url = req.url.replace(VersionId, 'v1');
    return handleRequest(req, options);
  },
  getLowQualityResultEntry(entryId: string, options?: AdditionalRequestOptions): Promise<LowQualitySearchHistoryEntry> {
    const req = { ...lowQualityResultEntryReqObj };
    req.url = req.url.replace(VersionId, 'v1');
    req.url = req.url.replace(EntryId, entryId);
    return handleRequest(req, options);
  },
  updateLowQualityResultEntry(
    payload: UpdateLowQualitySearchEntry,
    options?: AdditionalRequestOptions,
  ): Promise<LowQualitySearchHistoryEntry> {
    const req = { ...updateLowQualityResultEntryReqObj };
    req.url = req.url.replace(VersionId, 'v1');
    req.url = req.url.replace(EntryId, payload.id);
    req.body = payload as unknown as BodyInit;
    return handleRequest(req, options);
  },
};

const handleRequest = <T>(req: RequestOptions, options?: AdditionalRequestOptions): Promise<T> => {
  if (options?.headers) {
    req.headers = options.headers;
  }
  if (options?.baseUrl) {
    req.url = `${options.baseUrl}${req.url}`;
  }

  return request(req);
};

export default searchApi;
